<template>
  <dialog
    ref="dialogRef"
    :aria-labelledby="headerAriaId || undefined"
    :aria-modal="isModal ? 'true' : undefined"
    :class="dialogClasses"
    data-cy="ds-dialog"
    closedby="none"
    @mousedown="trackMouseDown"
    @click.self="requestToCloseDialog"
    @cancel.prevent="requestToCloseDialog"
    @keydown.esc="requestToCloseDialog"
  >
    <div
      ref="dialogContentRef"
      :class="DS_DIALOG_CLASSES.Content"
      data-cy="ds-dialog__content"
    >
      <header :class="DS_DIALOG_CLASSES.Header">
        <div :class="DS_DIALOG_CLASSES.HeaderContent">
          <slot name="header">
            <div
              v-if="shouldRenderFallbackHeader"
              :id="headerAriaId || undefined"
              :class="[DS_DIALOG_CLASSES.Title, DS_DIALOG_CLASSES.TitleText]"
              data-cy="ds-dialog__title"
            >
              {{ props.header }}
            </div>
            <div
              v-else
              class="skeleton__line skeleton__line--full gradient"
            ></div>
          </slot>

          <DSButton
            v-if="props.closeable"
            variant="ghost"
            size="md"
            aria-label="Close"
            prefix-icon="cross"
            data-cy="ds-dialog__close-button"
            @click="closeDialog"
          />
        </div>

        <slot name="subheader"></slot>
      </header>

      <div
        :class="DS_DIALOG_CLASSES.Body"
        data-cy="ds-dialog__body"
      >
        <slot />
      </div>

      <footer
        v-if="hasFooterSlotContent"
        :class="DS_DIALOG_CLASSES.Footer"
      >
        <slot name="footer" />
      </footer>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, provide, ref, useSlots, watch } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import {
  DS_DIALOG_CLASSES,
  DS_DIALOG_POSITION_CLASSES,
  DS_DIALOG_POSITIONS,
  DS_DIALOG_REF_PROVIDE_KEY,
  DS_DIALOG_TYPES,
} from "@/src/design_system/layouts_and_containers/dialog/constants";
import { useDialogCloseOnBackdrop } from "@/src/design_system/layouts_and_containers/dialog/composables/useDialogCloseOnBackdrop";
import type { DSDialogProps } from "@/src/design_system/layouts_and_containers/dialog/types";
import { DS_SIZE } from "@/src/design_system/shared/types/size";
import { generateUUID } from "@/src/shared/utils/uuid";
import { hasSlotContent } from "@/src/shared/utils/slots";
import { useFocusTrap } from "@/src/design_system/layouts_and_containers/dialog/composables/useFocusTrap";

const props = withDefaults(defineProps<DSDialogProps>(), {
  header: undefined,
  closeable: true,
  type: DS_DIALOG_TYPES.Modal,
  position: DS_DIALOG_POSITIONS.Center,
  size: DS_SIZE.Medium,
  absolute: false,
});

const openModelValue = defineModel<boolean>("open", { required: true });

const dialogRef = ref<HTMLDialogElement | null>(null);
const dialogContentRef = ref<HTMLDivElement | null>(null);
const slots = useSlots();

provide(DS_DIALOG_REF_PROVIDE_KEY, dialogRef);

const instanceId = `ds-dialog-${generateUUID()}`;

const isModal = computed(() => {
  return props.type === DS_DIALOG_TYPES.Modal;
});

const isCloseable = computed(() => {
  return props.closeable;
});

const dialogPositionClass = computed(() => {
  if (props.type !== DS_DIALOG_TYPES.Dialog) return undefined;

  return DS_DIALOG_POSITION_CLASSES[props.position];
});

const hasHeaderSlotContent = computed(() => {
  return hasSlotContent(slots, "header");
});

const hasFooterSlotContent = computed(() => {
  return hasSlotContent(slots, "footer");
});

const shouldRenderFallbackHeader = computed(() => {
  return Boolean(props.header) && !hasHeaderSlotContent.value;
});

const headerAriaId = computed(() => {
  return shouldRenderFallbackHeader.value ? `${instanceId}-header` : undefined;
});

const openDialog = () => {
  const dialog = dialogRef.value;
  if (!dialog) return;

  if (isModal.value) dialog.showModal();
  else dialog.show();
};

watch(
  openModelValue,
  isOpen => {
    const dialog = dialogRef.value;
    if (!dialog) return;

    if (isOpen) {
      openDialog();
    } else if (dialog.open) {
      dialog.close();
    }
  },
  { immediate: true }
);

watch(
  () => props.type,
  () => {
    if (!dialogRef.value) return;
    if (!openModelValue.value) return;

    openDialog();
  }
);

const closeDialogNative = () => {
  if (dialogRef.value?.open) {
    dialogRef.value.close();
  }
};

onBeforeUnmount(() => {
  closeDialogNative();
});

const closeDialog = () => {
  openModelValue.value = false;
};

const { closeOnBackdropClick, trackMouseDown } = useDialogCloseOnBackdrop({
  dialogContentRef,
  closeDialog,
});

const requestToCloseDialog = (event?: Event) => {
  if (!isCloseable.value || !isModal.value) return;

  closeOnBackdropClick(event);
};

const dialogClasses = computed(() => {
  const classes = [DS_DIALOG_CLASSES.Root, dialogPositionClass.value];

  if (isModal.value) {
    classes.push(DS_DIALOG_CLASSES.Modal);
  } else {
    classes.push(DS_DIALOG_CLASSES.Dialog);
  }

  if (props.absolute) {
    classes.push(DS_DIALOG_CLASSES.Absolute);
  }

  if (props.size) {
    classes.push(`${DS_DIALOG_CLASSES.Root}--${props.size}`);
  }

  return classes;
});

useFocusTrap({ dialogRef, isModal });

onMounted(() => {
  if (openModelValue.value) {
    openDialog();
  }
});

defineExpose({
  close: closeDialog,
});
</script>
